/**
 * True superellipse (Lamé curve) rounded rectangle clipping.
 *
 * Mirrors the backend canvas-compositor squircle implementation
 * (workers/segment-converter-modal/renderer/src/lib/squircle.ts)
 * but outputs SVG path data for use with CSS `clip-path: path(...)`.
 *
 * A superellipse is defined by |x/a|^n + |y/b|^n = 1 where n controls
 * the shape between a diamond (n=1), circle (n=2), and rectangle (n→∞).
 * For n > 2 the shape is a "squircle" — a rounded rectangle with smooth
 * continuous-curvature corners that avoid the abrupt tangent break of
 * standard circular arcs.
 */

export const SUPERELLIPSE_N = 5;

// ---------------------------------------------------------------------------
// CSS border-radius → superellipse radius conversion
// ---------------------------------------------------------------------------

/**
 * Precomputed scale factor: how much larger a superellipse radius must be
 * to produce the same visible "corner depth" as a CSS circular border-radius.
 *
 * For n=5: factor ≈ 2.253
 */
const CSS_RADIUS_SCALE_FACTOR = (() => {
  const circleCornerDepth = 1 - Math.cos(Math.PI / 4);
  const superellipseCornerDepth = 1 - Math.pow(Math.cos(Math.PI / 4), 2 / SUPERELLIPSE_N);
  return circleCornerDepth / superellipseCornerDepth;
})();

/**
 * Convert a CSS border-radius (designed for circular arcs) into an equivalent
 * superellipse radius that produces the same visible corner rounding.
 */
export function cssRadiusToSuperellipseRadius(cssRadius: number): number {
  if (cssRadius <= 0) return 0;
  return cssRadius * CSS_RADIUS_SCALE_FACTOR;
}

/**
 * Generate an SVG path `d` attribute string for a superellipse rounded rect.
 *
 * The path is suitable for CSS `clip-path: path('...')` where coordinates
 * are relative to the element's own content box (0,0 is top-left).
 *
 * Returns null if radius is 0 (caller should skip clip-path entirely).
 */
export function squircleClipPathData(
  rectWidth: number,
  rectHeight: number,
  radius: number,
  exponent: number = SUPERELLIPSE_N,
): string | null {
  const maxRadius = Math.min(rectWidth, rectHeight) / 2;
  const safeRadius = Math.max(0, Math.min(radius, maxRadius));

  if (safeRadius <= 0) return null;

  const segmentsPerCorner = Math.max(16, Math.min(64, Math.ceil(safeRadius / 2)));
  const twoOverN = 2 / exponent;
  const right = rectWidth;
  const bottom = rectHeight;
  const posX = 0;
  const posY = 0;

  const parts: string[] = [];

  // Start at top edge, after top-left corner
  parts.push(`M ${posX + safeRadius} ${posY}`);

  // Top edge
  parts.push(`L ${right - safeRadius} ${posY}`);

  // Top-right corner
  {
    const centerX = right - safeRadius;
    const centerY = posY + safeRadius;
    for (let step = 1; step <= segmentsPerCorner; step++) {
      const theta = (step / segmentsPerCorner) * (Math.PI / 2);
      const powSin = Math.pow(Math.abs(Math.sin(theta)), twoOverN);
      const powCos = Math.pow(Math.abs(Math.cos(theta)), twoOverN);
      parts.push(`L ${centerX + safeRadius * powSin} ${centerY - safeRadius * powCos}`);
    }
  }

  // Right edge
  parts.push(`L ${right} ${bottom - safeRadius}`);

  // Bottom-right corner
  {
    const centerX = right - safeRadius;
    const centerY = bottom - safeRadius;
    for (let step = 1; step <= segmentsPerCorner; step++) {
      const theta = (step / segmentsPerCorner) * (Math.PI / 2);
      const powSin = Math.pow(Math.abs(Math.sin(theta)), twoOverN);
      const powCos = Math.pow(Math.abs(Math.cos(theta)), twoOverN);
      parts.push(`L ${centerX + safeRadius * powCos} ${centerY + safeRadius * powSin}`);
    }
  }

  // Bottom edge
  parts.push(`L ${posX + safeRadius} ${bottom}`);

  // Bottom-left corner
  {
    const centerX = posX + safeRadius;
    const centerY = bottom - safeRadius;
    for (let step = 1; step <= segmentsPerCorner; step++) {
      const theta = (step / segmentsPerCorner) * (Math.PI / 2);
      const powSin = Math.pow(Math.abs(Math.sin(theta)), twoOverN);
      const powCos = Math.pow(Math.abs(Math.cos(theta)), twoOverN);
      parts.push(`L ${centerX - safeRadius * powSin} ${centerY + safeRadius * powCos}`);
    }
  }

  // Left edge
  parts.push(`L ${posX} ${posY + safeRadius}`);

  // Top-left corner
  {
    const centerX = posX + safeRadius;
    const centerY = posY + safeRadius;
    for (let step = 1; step <= segmentsPerCorner; step++) {
      const theta = (step / segmentsPerCorner) * (Math.PI / 2);
      const powSin = Math.pow(Math.abs(Math.sin(theta)), twoOverN);
      const powCos = Math.pow(Math.abs(Math.cos(theta)), twoOverN);
      parts.push(`L ${centerX - safeRadius * powCos} ${centerY - safeRadius * powSin}`);
    }
  }

  parts.push("Z");
  return parts.join(" ");
}

// ---------------------------------------------------------------------------
// Canvas 2D squircle path (identical to backend canvas-compositor squircle.ts)
// ---------------------------------------------------------------------------

/**
 * Draw a superellipse rounded rectangle directly onto a Canvas 2D context.
 *
 * This is a direct port of the backend renderer's squirclePath function
 * (workers/segment-converter-modal/renderer/src/lib/squircle.ts).
 * The path is added to the context but NOT stroked or filled.
 */
export function squirclePathCanvas2D(
  context: CanvasRenderingContext2D | OffscreenCanvasRenderingContext2D,
  posX: number,
  posY: number,
  rectWidth: number,
  rectHeight: number,
  radius: number,
  exponent: number = SUPERELLIPSE_N,
): void {
  const maxRadius = Math.min(rectWidth, rectHeight) / 2;
  const safeRadius = Math.max(0, Math.min(radius, maxRadius));

  if (safeRadius === 0) {
    context.beginPath();
    context.rect(posX, posY, rectWidth, rectHeight);
    return;
  }

  const segmentsPerCorner = Math.max(16, Math.min(64, Math.ceil(safeRadius / 2)));
  const twoOverN = 2 / exponent;
  const right = posX + rectWidth;
  const bottom = posY + rectHeight;

  context.beginPath();
  context.moveTo(posX + safeRadius, posY);

  // Top edge
  context.lineTo(right - safeRadius, posY);

  // Top-right corner
  {
    const centerX = right - safeRadius;
    const centerY = posY + safeRadius;
    for (let step = 1; step <= segmentsPerCorner; step++) {
      const theta = (step / segmentsPerCorner) * (Math.PI / 2);
      const powSin = Math.pow(Math.abs(Math.sin(theta)), twoOverN);
      const powCos = Math.pow(Math.abs(Math.cos(theta)), twoOverN);
      context.lineTo(centerX + safeRadius * powSin, centerY - safeRadius * powCos);
    }
  }

  // Right edge
  context.lineTo(right, bottom - safeRadius);

  // Bottom-right corner
  {
    const centerX = right - safeRadius;
    const centerY = bottom - safeRadius;
    for (let step = 1; step <= segmentsPerCorner; step++) {
      const theta = (step / segmentsPerCorner) * (Math.PI / 2);
      const powSin = Math.pow(Math.abs(Math.sin(theta)), twoOverN);
      const powCos = Math.pow(Math.abs(Math.cos(theta)), twoOverN);
      context.lineTo(centerX + safeRadius * powCos, centerY + safeRadius * powSin);
    }
  }

  // Bottom edge
  context.lineTo(posX + safeRadius, bottom);

  // Bottom-left corner
  {
    const centerX = posX + safeRadius;
    const centerY = bottom - safeRadius;
    for (let step = 1; step <= segmentsPerCorner; step++) {
      const theta = (step / segmentsPerCorner) * (Math.PI / 2);
      const powSin = Math.pow(Math.abs(Math.sin(theta)), twoOverN);
      const powCos = Math.pow(Math.abs(Math.cos(theta)), twoOverN);
      context.lineTo(centerX - safeRadius * powSin, centerY + safeRadius * powCos);
    }
  }

  // Left edge
  context.lineTo(posX, posY + safeRadius);

  // Top-left corner
  {
    const centerX = posX + safeRadius;
    const centerY = posY + safeRadius;
    for (let step = 1; step <= segmentsPerCorner; step++) {
      const theta = (step / segmentsPerCorner) * (Math.PI / 2);
      const powSin = Math.pow(Math.abs(Math.sin(theta)), twoOverN);
      const powCos = Math.pow(Math.abs(Math.cos(theta)), twoOverN);
      context.lineTo(centerX - safeRadius * powCos, centerY - safeRadius * powSin);
    }
  }

  context.closePath();
}
